import React, { useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { awsConfig } from './aws-config';
import { useAuthStore } from 'store/authStore';
import Login from 'components/Login';
import AppHeader from 'components/AppHeader';
import AppSelect from 'components/AppSelect';
import ScanShippingBox from 'components/ScanShippingBox';
import ScanItem from 'components/ScanItem';
import ConfirmItem from 'components/ConfirmItem';
import GradeItem from 'components/GradeItem';
import ConfirmClose from 'components/ConfirmClose';
import { Card } from 'components/ui/card';
import './index.css';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useTrackingStore } from './store';
import { useProductStore } from './store/productStore';
import { useBarcodeScan } from 'hooks/useBarcodeScan';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { getFedexId, scanId } from 'services/barcodes/checkFedexId';
import { BarcodeType } from 'types';
import Receiving from './components/Receiving';
import { ReceivingDisposition } from './components/ReceivingDisposition';
import { cn } from './utils/classnames';
import { useArrivalReceiptStore } from './store/arrivalReceiptStore';
import { GradingDisabledScreen } from './components/GradingDisabledScreen';
import { GradingDispositionSheet } from './components/GradingDispositionSheet';
import { ArrivalDispositionSheet } from './components/ArrivalDispositionSheet';

// TODO: barcodeDebugMessage and everything related to this will be removed once we approve barcode features work correctly

Amplify.configure(awsConfig);

const BARCODE_LISTENER_SCREENS = [
  SCREEN_TYPES.GRADE_ITEM,
  SCREEN_TYPES.CONFIRM_ITEM,
  SCREEN_TYPES.CONFIRM_CLOSE,
  SCREEN_TYPES.RECEIVING_DISPOSITION,
];

function App() {
  const { status, stationId, restoreSession } = useAuthStore();
  const { currentScreen, setBarcodeDebugMessage } = useAppStore();
  const { setProductNew, currentProduct, setProductCondition, setProductId, setCurrentProduct } =
    useProductStore();
  const { setCurrentTrackingId } = useTrackingStore();
  const { currentArrivalReceipt } = useArrivalReceiptStore();

  const handleBarcodeDetected = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();

      const fedexId = getFedexId(trimmedValue);
      if (fedexId !== trimmedValue) {
        setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
        void scanId(trimmedValue);
        return;
      }

      // Check for other barcode patterns
      const barcodeType = isInputBarcode(trimmedValue) as BarcodeType | null;
      if (barcodeType) {
        setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
        getBarcodeAction(barcodeType, currentScreen, {
          setProductNew,
          setProductCondition,
          setBarcodeDebugMessage,
          setProductId,
          setTrackingId: setCurrentTrackingId,
          setCurrentProduct,
        });
      } else {
        setBarcodeDebugMessage(
          `Input not recognized as FedEx, tracking ID, or barcode. Input -> ${trimmedValue}`
        );
        console.log('Input not recognized as FedEx, tracking ID, or barcode:', trimmedValue);
      }
    },
    [
      currentScreen,
      setProductNew,
      setProductCondition,
      setBarcodeDebugMessage,
      setProductId,
      setCurrentProduct,
      setCurrentTrackingId,
    ]
  );

  useBarcodeScan({
    onBarcode: handleBarcodeDetected,
    enabled: BARCODE_LISTENER_SCREENS.includes(currentScreen as any),
    setBarcodeDebugMessage,
  });

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  if (status === 'loading') {
    return <div className="app-container">Loading...</div>;
  }

  if (status !== 'authenticated' || !stationId) {
    return (
      <div className="app-container">
        <Login />
      </div>
    );
  }

  const isUnexpected =
    currentProduct?.product_id === 'Unknown' || currentProduct?.expected_ind === 'n';

  const isCallback =
    currentProduct?.exception_process === 'callback' ||
    currentProduct?.disposition_stage === 'callback';
  const isException = currentArrivalReceipt?.responseState === 'exception';
  const isProblemState = currentArrivalReceipt?.responseState === 'problem' || isUnexpected;

  const dispositionAlert: keyof typeof DISPOSITION_COLORS | null = isCallback
    ? 'CALLBACK'
    : isException
      ? 'EXCEPTION'
      : isProblemState
        ? 'PROBLEM'
        : null;

  const getCurrentScreenComponent = (): React.ReactNode => {
    switch (currentScreen) {
      case SCREEN_TYPES.APP_SELECT:
        return <AppSelect />;
      case SCREEN_TYPES.SCAN_BOX:
        return <ScanShippingBox />;
      case SCREEN_TYPES.SCAN_ITEM:
        return <ScanItem />;
      case SCREEN_TYPES.CONFIRM_ITEM:
        return <ConfirmItem />;
      case SCREEN_TYPES.GRADE_ITEM:
        return <GradeItem />;
      case SCREEN_TYPES.CONFIRM_CLOSE:
        return <ConfirmClose />;
      case SCREEN_TYPES.RECEIVING:
        return <Receiving />;
      case SCREEN_TYPES.RECEIVING_DISPOSITION:
        return <ReceivingDisposition />;
      case SCREEN_TYPES.GRADING_DISABLED:
        return <GradingDisabledScreen />;

      default:
        return <AppSelect />;
    }
  };

  return (
    <div className="app-container">
      <div className="w-full max-w-[860px]">
        <Card
          className={cn(
            'scanner-card flex flex-col overflow-hidden rounded-3xl bg-white px-0 py-0 shadow-[0_10px_40px_rgba(0,0,0,0.1)]',
            dispositionAlert && DISPOSITION_COLORS[dispositionAlert].cardBorder
          )}
        >
          <AppHeader />
          {/* <CardHeader className="bg-slate-100">{barcodeDebugMessage}</CardHeader> */}
          <div
            className={cn(
              'flex-1 min-h-0 overflow-y-auto px-4 py-4',
              dispositionAlert && DISPOSITION_COLORS[dispositionAlert].gradient
            )}
          >
            {getCurrentScreenComponent()}
          </div>
          <GradingDispositionSheet />
          <ArrivalDispositionSheet />
        </Card>
      </div>
    </div>
  );
}

export default App;
